import { useState, useEffect } from 'react';
import { useParallaxFrames } from '@/hooks/useParallaxFrames';
import { DRINK_VARIANTS } from '@/config/drinkVariants';
import LoadingScreen from '@/components/LoadingScreen';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import ProductSection from '@/components/ProductSection';
import IngredientsSection from '@/components/IngredientsSection';
import NutritionSection from '@/components/NutritionSection';
import ReviewsSection from '@/components/ReviewsSection';
import FAQSection from '@/components/FAQSection';
import CTASection from '@/components/CTASection';
import Footer from '@/components/Footer';
import ScrollRevealSection from '@/components/ScrollRevealSection';
import SEO from '@/components/SEO';
const Index = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);
  const [currentProduct, setCurrentProduct] = useState('g-drink');
  
  const currentVariant = DRINK_VARIANTS[currentVariantIndex];
  
  const { 
    frames, 
    currentFrame, 
    loadingProgress, 
    isLoaded, 
    framesComplete,
    slideProgress,
    resetFrames,
    totalScrollHeight,
  } = useParallaxFrames(
    currentVariant.frameBaseUrl,
    currentVariant.totalFrames,
    !isLoading,
    currentVariant.frameDelayPattern
  );

  // Update CSS custom properties for theme color when variant changes
  useEffect(() => {
    document.documentElement.style.setProperty('--primary', currentVariant.themeColor);
    document.documentElement.style.setProperty('--accent', currentVariant.themeColor);
    document.documentElement.style.setProperty('--ring', currentVariant.themeColor);
  }, [currentVariant.themeColor]);

  // Transition from loading when frames are loaded AND belong to current variant
  useEffect(() => {
    if (
      isLoaded &&
      isLoading &&
      frames.length > 0 &&
      frames[0] &&
      frames[0].src.startsWith(currentVariant.frameBaseUrl)
    ) {
      setIsLoading(false);
    }
  }, [isLoaded, isLoading, frames, currentVariant.frameBaseUrl]);

  const handlePrevVariant = () => {
    if (currentVariantIndex > 0) {
      setIsLoading(true);
      setCurrentVariantIndex(currentVariantIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleNextVariant = () => {
    if (currentVariantIndex < DRINK_VARIANTS.length - 1) {
      setIsLoading(true);
      setCurrentVariantIndex(currentVariantIndex + 1);
      window.scrollTo(0, 0);
    }
  };

  // Calculate if we're past the slide-up transition
  const isPastTransition = slideProgress >= 1;

  return (
    <div className="dark" key={currentVariant.frameBaseUrl}>
      <SEO
        title="DLC Group | G Drink, Capri-Sun, Stimorol & More in Palestine"
        description="Discover the world's most-loved food and beverage brands across Palestine — G Drink, Capri-Sun, Stimorol, Durukan. Cash on delivery, free shipping over ₪120."
        path="/"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'DLC Group',
            url: 'https://dlcgroup.ps',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://dlcgroup.ps/?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: `G Drink ${currentVariant.name}`,
            description: currentVariant.description,
            brand: { '@type': 'Brand', name: 'G Drink' },
            offers: {
              '@type': 'Offer',
              priceCurrency: 'ILS',
              price: currentVariant.price,
              availability: 'https://schema.org/InStock',
              url: 'https://dlcgroup.ps/',
            },
          },
        ]}
      />
      {/* Loading screen */}
      {isLoading && (
        <LoadingScreen
          progress={loadingProgress}
          onComplete={() => {}}
          currentVariant={currentVariant}
        />
      )}

      {/* Main content */}
      {!isLoading && (
        <main className="animate-fade-in">
          <Navbar currentProduct={currentProduct} onProductChange={setCurrentProduct} />
          
          {/* Scroll spacer - provides scroll room for frame animation + slide-up */}
          <div data-hero-spacer style={{ height: `${totalScrollHeight}px` }} />
          
          {/* Fixed hero - always visible behind content until slide-up complete */}
          {!isPastTransition && (
            <HeroSection
              frames={frames}
              currentFrame={currentFrame}
              currentVariant={currentVariant}
              variantIndex={currentVariantIndex}
              totalVariants={DRINK_VARIANTS.length}
              onPrev={handlePrevVariant}
              onNext={handleNextVariant}
            />
          )}
          
          {/* Content sections - slide up over hero based on scroll, then flow normally */}
          <ScrollRevealSection 
            framesComplete={framesComplete}
            slideProgress={slideProgress}
          >
            <ProductSection currentVariant={currentVariant} />
            <IngredientsSection currentVariant={currentVariant} />
            <NutritionSection currentVariant={currentVariant} />
            <ReviewsSection currentVariant={currentVariant} />
            <FAQSection currentVariant={currentVariant} />
            <CTASection currentVariant={currentVariant} />
            <Footer />
          </ScrollRevealSection>
        </main>
      )}
    </div>
  );
};

export default Index;
